import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus, Clock } from 'lucide-react';

export const RegisterPage = () => {
  const [form, setForm] = useState({
    email: '', login: '', nom: '', prenom: '', password: '', confirmPassword: '',
  });
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);   // compte créé, en attente
  const { register } = useAuth();

  const handleChange = (e) =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    const result = await register(form.email, form.password, form.login, form.nom, form.prenom);
    setLoading(false);

    if (result.success && result.pending) {
      setPending(true);          // affiche l'écran d'attente
    } else if (result.success) {
      // Ne devrait plus arriver, mais au cas où
      setPending(true);
    } else {
      setError(result.error);
    }
  };

  const fields = [
    { name: 'prenom',          label: 'Prénom',                    type: 'text',     testId: 'register-prenom-input' },
    { name: 'nom',             label: 'Nom',                       type: 'text',     testId: 'register-nom-input' },
    { name: 'login',           label: 'Login',                     type: 'text',     testId: 'register-login-input' },
    { name: 'email',           label: 'Email',                     type: 'email',    testId: 'register-email-input' },
    { name: 'password',        label: 'Mot de passe',              type: 'password', testId: 'register-password-input' },
    { name: 'confirmPassword', label: 'Confirmer le mot de passe', type: 'password', testId: 'register-confirm-input' },
  ];

  // ── Écran de confirmation ──────────────────────────────────────────────────
  if (pending) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6"
        style={{ backgroundColor: 'var(--bg-page)' }}>
        <div className="w-full max-w-md border p-10 text-center"
          style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)' }}>
          <div className="flex items-center justify-center w-16 h-16 rounded-full mx-auto mb-6"
            style={{ backgroundColor: 'var(--bg-subtle)' }}>
            <Clock size={32} style={{ color: 'var(--forest)' }} />
          </div>
          <h1 className="text-2xl font-bold mb-3"
            style={{ color: 'var(--forest)', fontFamily: 'Cormorant Garamond, serif' }}>
            Inscription envoyée
          </h1>
          <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
            Votre compte a bien été créé. Un administrateur doit valider votre
            inscription avant que vous puissiez vous connecter.
          </p>
          <p className="text-xs mb-6" style={{ color: 'var(--text-muted)' }}>
            Vous recevrez une confirmation dès que votre compte sera activé.
          </p>
          <Link to="/login"
            className="text-sm font-medium hover:underline"
            style={{ color: 'var(--terracotta)' }}>
            Retour à la connexion
          </Link>
        </div>
      </div>
    );
  }

  // ── Formulaire d'inscription ───────────────────────────────────────────────
  return (
    <div data-testid="register-page" className="min-h-screen flex items-center justify-center p-6"
      style={{ backgroundColor: 'var(--bg-page)' }}>
      <div className="w-full max-w-md">
        <div className="border p-8" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border)' }}>

          <div className="flex items-center justify-center mb-8">
            <UserPlus size={30} className="mr-3" style={{ color: 'var(--forest)' }} />
            <h1 className="text-3xl font-bold"
              style={{ color: 'var(--forest)', fontFamily: 'Cormorant Garamond, serif' }}>
              Inscription
            </h1>
          </div>

          {/* Avertissement validation */}
          <div className="text-xs px-3 py-2 rounded-sm mb-5 border flex items-start gap-2"
            style={{
              backgroundColor: 'var(--bg-subtle)',
              borderColor: 'var(--border)',
              color: 'var(--text-muted)',
            }}>
            <Clock size={13} className="shrink-0 mt-0.5" />
            Votre compte devra être validé par un administrateur avant de pouvoir vous connecter.
          </div>

          {error && (
            <div data-testid="register-error"
              className="px-4 py-3 mb-4 text-sm border"
              style={{
                backgroundColor: 'rgba(168,87,60,0.08)',
                borderColor: 'var(--terracotta)',
                color: 'var(--terracotta)',
              }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {fields.map(({ name, label, type, testId }) => (
              <div key={name}>
                <Label htmlFor={name} className="block mb-1.5 text-sm font-medium"
                  style={{ color: 'var(--text-secondary)' }}>
                  {label}
                </Label>
                <Input
                  data-testid={testId}
                  id={name} name={name} type={type}
                  value={form[name]} onChange={handleChange}
                  required
                />
              </div>
            ))}

            <Button type="submit" disabled={loading}
              className="w-full py-5 font-semibold text-white mt-2"
              style={{ backgroundColor: 'var(--forest)' }}>
              {loading ? 'Inscription…' : "S'inscrire"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link to="/login" data-testid="login-link"
              className="text-sm font-medium hover:underline"
              style={{ color: 'var(--terracotta)' }}>
              Déjà un compte ? Se connecter
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};